import { readChatActorId, readChatSessionIdentity } from 'src/auth/sessionEvents';
// useMarkRead (issue #419) — POST /chats/:chatId/read.
//
// Optimistically zeros the caller's `unreadCount` on the cached inbox row
// for `chatId` so the badge clears the moment the row is opened. The server
// response refreshes the participant row on settle.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { markChatRead, SupportInboxScope } from './api';
import { chatKeys } from './keys';

import type { Chat } from 'src/models/chat';

interface MarkReadArgs {
  chatId: number;
  messageId: number;
}

export function useMarkRead(scope: SupportInboxScope = 'legacy') {
  const queryClient = useQueryClient();
  const sessionIdentity = readChatSessionIdentity();
  return useMutation(
    ({ chatId, messageId }: MarkReadArgs) => {
      if (readChatSessionIdentity() !== sessionIdentity) throw new Error('Chat session changed; reopen the conversation.');
      return markChatRead(chatId, { messageId }, scope);
    },
    {
      onMutate: async ({ chatId }: MarkReadArgs) => {
        if (readChatSessionIdentity() !== sessionIdentity) throw new Error('Chat session changed; reopen the conversation.');
        const root = chatKeys.myChatsRoot();
        const queries = queryClient.getQueriesData<Chat[]>(root);
        queries.forEach(([key, value]) => {
          if (!value) return;
          queryClient.setQueryData<Chat[]>(
            key,
            value.map((row) => {
              if (row.id !== chatId) return row;
              const participants = row.participants?.map((p) =>
                p.userId === readChatActorId() ? { ...p, unreadCount: 0 } : p
              );
              return { ...row, participants };
            })
          );
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries(chatKeys.myChatsRoot());
        // Event Threads hub badges come from their own query (#4005).
        queryClient.invalidateQueries(chatKeys.eventThreadsRoot());
      },
    }
  );
}
